import {
  normalizeRedactionStatus,
  REDACTION_STATUS_TAG,
} from '@consts/attachmentRedaction'
import appStore from '@stores/appStore'
import { Tag, Tooltip } from 'antd'
import { useState } from 'react'

import StatusDetailModal from './StatusDetailModal'

interface Props {
  status: unknown
  /** Unsupported/Failed reason — only present for onshore users (BE strips it for offshore). */
  reason?: string | null
  /**
   * When given, the tag opens the reason and history for this attachment. Left out on
   * surfaces that already show the reason, or where a modal would be in the way.
   */
  attachmentId?: string
}

/**
 * Status badge for the automatic redaction pipeline. Renders nothing for legacy
 * attachments (status None) so existing tables stay unchanged.
 *
 * With an attachmentId it becomes clickable: the status alone says a document is not
 * available to offshore staff, but not why, and "why" is what an admin needs before they
 * can do anything about it. Reasons are not carried on list responses — they are fetched
 * when someone asks for one.
 *
 * Offshore staff never get the detail. They are shown that a document exists and is not
 * available to them; the reason can name what the pipeline found inside it.
 */
const AttachmentRedactionTag = ({ status, reason, attachmentId }: Props) => {
  const [detailOpen, setDetailOpen] = useState(false)

  const tagConfig = REDACTION_STATUS_TAG[normalizeRedactionStatus(status)]
  if (!tagConfig) {
    return null
  }

  const canOpenDetail = !!attachmentId && !appStore.ifOffshoreStaff()

  const tag = (
    <Tag
      color={tagConfig.color}
      className={`mr-0 ${canOpenDetail ? 'cursor-pointer' : ''}`}
      onClick={
        canOpenDetail
          ? e => {
              // These tags sit inside file rows and table cells that carry their own
              // click behaviour; opening the details must not also open the document.
              e.stopPropagation()
              setDetailOpen(true)
            }
          : undefined
      }
    >
      {tagConfig.label}
    </Tag>
  )

  const tooltip = reason ?? (canOpenDetail ? 'Click for details' : undefined)

  return (
    <>
      {tooltip ? <Tooltip title={tooltip}>{tag}</Tooltip> : tag}

      {detailOpen && (
        <StatusDetailModal
          attachmentId={attachmentId}
          fallbackStatus={status}
          onClose={() => setDetailOpen(false)}
        />
      )}
    </>
  )
}

export default AttachmentRedactionTag
